import {
  type PlannedTime,
  type TimeOff,
  LeaveType,
  PlannedTimeFrequency,
} from "@/helpers/project/typesDefinition";
import type {
  PlanningProject,
  PlanningUser,
  VisibleDay,
} from "@/views/planning/type";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFAULT_DURATION_PER_DAY = 480;
export const DEFAULT_LEAVE_TYPE = LeaveType.PAID_LEAVE;

// ---------------------------------------------------------------------------
// Shared types used across Planning views
// ---------------------------------------------------------------------------

export type PlanningFrequency = PlannedTimeFrequency;

export type PlanningProjectRole = {
  id: string;
  name: string;
};

export type PlanningPlannedTime = Omit<
  PlannedTime,
  | "startDate"
  | "endDate"
  | "repeatEndDate"
  | "frequency"
  | "user"
  | "project"
  | "projectRole"
> & {
  id: string;
  periodAndRecurrenceId: string;
  active: boolean;
  startDate: string;
  endDate: string;
  frequency: PlanningFrequency;
  repeatEndDate: string | null;
  durationPerDay: number;
  tentative: boolean;
  commercialStatusByDate?: CommercialStatusEntry[] | null;
  status?: string | null;
  notes?: string | null;
  adjustedHours?: number | null;
  user: PlanningUser;
  project?: PlanningProject | null;
  projectRole?: PlanningProjectRole | null;
};

export type CommercialStatusEntry = {
  date: string;
  status: "uncovered" | "pending" | "committed" | "secured";
  totalAmount: number;
  securedAmount: number;
  committedAmount: number;
  pendingAmount: number;
  uncoveredAmount: number;
  tooltip: string | null;
  trackingMode?: string | null;
};

export type PlanningTimeOff = Omit<
  TimeOff,
  "startDate" | "endDate" | "repeatEndDate" | "frequency" | "user" | "type"
> & {
  id: string;
  periodAndRecurrenceId: string;
  active: boolean;
  startDate: string;
  endDate: string;
  frequency: PlanningFrequency;
  repeatEndDate: string | null;
  durationPerDay: number;
  user: PlanningUser;
  type?: string | null;
  timeOffType?: string | null;
};

export type PlanningHoliday = {
  id: string;
  date: string;
  name: string | null;
};

export type PlanningTimesheetEntry = {
  id: string;
  date: string;
  duration: number;
  notes: string | null;
  user: PlanningUser;
  project: PlanningProject;
  theme: { id: string; name: string };
  projectRole: { id: string; name: string };
};

export type TimesheetSuggestion = {
  id: string;
  user: PlanningUser;
  day: VisibleDay;
  plannedTime: PlanningPlannedTime;
  hours: number;
};

// ---------------------------------------------------------------------------
// Raw GraphQL response types
// ---------------------------------------------------------------------------

export interface RawPlanningProject {
  id: string;
  name: string;
  color: string | null;
}

export interface RawPlannedTime {
  id: string;
  periodAndRecurrenceId: string;
  active?: boolean | null;
  startDate: string;
  endDate: string;
  frequency: PlanningFrequency;
  repeatEndDate: string | null;
  durationPerDay: number;
  tentative?: boolean | null;
  notes?: string | null;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  project: {
    id: string;
    name: string;
    color: string | null;
  } | null;
  projectRole: {
    id: string;
    name: string;
  } | null;
  commercialStatusByDate?: CommercialStatusEntry[] | null;
}

export interface RawTimeOff {
  id: string;
  periodAndRecurrenceId: string;
  active?: boolean | null;
  startDate: string;
  endDate: string;
  frequency: PlanningFrequency;
  repeatEndDate: string | null;
  durationPerDay: number;
  type?: string | null;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface RawHoliday {
  id: string;
  date: string;
  name: string | null;
}

export interface RawPlanningTimesheetEntry {
  id: string;
  date: string;
  duration: number;
  notes: string | null;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    avatarUrl?: string | null;
    workDayHours?: number[] | null;
    email?: string | null;
  } | null;
  project: {
    id: string;
    name: string;
    color: string | null;
  } | null;
  theme: {
    id: string;
    name: string;
  } | null;
  projectRole: {
    id: string;
    name: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Normalization functions
// ---------------------------------------------------------------------------

export function normalizeProjects(
  rawProjects: RawPlanningProject[],
): PlanningProject[] {
  return rawProjects.map((project) => ({
    id: project.id,
    name: project.name,
    color: project.color ?? null,
  }));
}

export function normalizePlannedTimes(
  rawPlannedTimes: RawPlannedTime[],
): PlanningPlannedTime[] {
  const filtered = rawPlannedTimes.filter((plannedTime) => {
    // return hasUser?
    return Boolean(plannedTime.user?.id);
  });

  return filtered.map((plannedTime) => ({
    id: plannedTime.id,
    periodAndRecurrenceId: plannedTime.periodAndRecurrenceId,
    active: plannedTime.active ?? true,
    startDate: plannedTime.startDate,
    endDate: plannedTime.endDate,
    frequency: plannedTime.frequency,
    repeatEndDate: plannedTime.repeatEndDate ?? null,
    durationPerDay: plannedTime.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
    tentative: plannedTime.tentative ?? false,
    notes: plannedTime.notes ?? null,
    commercialStatusByDate: plannedTime.commercialStatusByDate ?? [],
    user: {
      id: plannedTime.user!.id,
      firstName: plannedTime.user!.firstName,
      lastName: plannedTime.user!.lastName,
    },
    project: plannedTime.project
      ? {
          id: plannedTime.project.id,
          name: plannedTime.project.name,
          color: plannedTime.project.color ?? null,
        }
      : null,
    projectRole: plannedTime.projectRole
      ? {
          id: plannedTime.projectRole.id,
          name: plannedTime.projectRole.name,
        }
      : null,
  }));
}

export function normalizeTimeOffs(
  rawTimeOffs: RawTimeOff[],
): PlanningTimeOff[] {
  const filtered = rawTimeOffs.filter((timeOff) => {
    // return hasUser?
    return Boolean(timeOff.user?.id);
  });

  return filtered.map((timeOff) => ({
    id: timeOff.id,
    periodAndRecurrenceId: timeOff.periodAndRecurrenceId,
    active: timeOff.active ?? true,
    startDate: timeOff.startDate,
    endDate: timeOff.endDate,
    frequency: timeOff.frequency,
    repeatEndDate: timeOff.repeatEndDate ?? null,
    durationPerDay: timeOff.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
    type: timeOff.type ?? DEFAULT_LEAVE_TYPE,
    user: {
      id: timeOff.user!.id,
      firstName: timeOff.user!.firstName,
      lastName: timeOff.user!.lastName,
    },
  }));
}

export function normalizeHolidaysByUser(
  userHolidaysMap: Array<{
    userId: string;
    holidays: RawHoliday[];
  }>,
): Record<string, PlanningHoliday[]> {
  const map: Record<string, PlanningHoliday[]> = {};

  for (const entry of userHolidaysMap) {
    map[entry.userId] = entry.holidays.map((holiday) => ({
      id: holiday.id,
      date: holiday.date,
      name: holiday.name,
    }));
  }

  return map;
}

export function normalizeTimesheetEntries(
  rawEntries: RawPlanningTimesheetEntry[],
): PlanningTimesheetEntry[] {
  return rawEntries
    .filter((entry) =>
      Boolean(
        entry.user?.id &&
          entry.project?.id &&
          entry.theme?.id &&
          entry.projectRole?.id,
      ),
    )
    .map((entry) => ({
      id: entry.id,
      date: entry.date,
      duration: entry.duration,
      notes: entry.notes,
      user: {
        id: entry.user!.id,
        firstName: entry.user!.firstName,
        lastName: entry.user!.lastName,
        avatarUrl: entry.user!.avatarUrl ?? null,
        workDayHours: entry.user!.workDayHours ?? null,
        email: entry.user!.email ?? null,
      },
      project: {
        id: entry.project!.id,
        name: entry.project!.name,
        color: entry.project!.color ?? null,
      },
      theme: {
        id: entry.theme!.id,
        name: entry.theme!.name,
      },
      projectRole: {
        id: entry.projectRole!.id,
        name: entry.projectRole!.name,
      },
    }));
}
